import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackEvent } from '../../../client/analytics';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { PageLocation } from '../../utils/tenantPlacementUtils';

const EVENT_IDS = {
  LISTING_MODULE_UPSELL_BANNER_LEARN_MORE: '6756',
  LISTING_MODULE_UPSELL_BANNER: '6755',
  DASHBOARD_UPSELL_BANNER: '6462',
  DASHBOARD_UPSELL_BANNER_LEARN_MORE: '6398',
  REACTIVATE_LISTING_UPSELL_MODAL: '6460',
  REACTIVATION_LEARN_MORE_BUTTON: '6396',
  REACTIVATE_LISTING_BUTTON: '6395',
  NEW_LISTING_UPSELL_MODAL: '6459',
  NEW_LISTING_LEARN_MORE_BUTTON: '6394',
  POST_NEW_LISTING_BUTTON: '6393',
  GO_TO_DOORSTEAD: '6388',
  GO_TO_NOMAD: '7509',
  STATE_NOT_SUPPORTED: '7510',
  VIEW_PAGE: '6437',
} as const;

// https://dataportal.zillowgroup.net/event-registry/6756
const TenantPlacementListingModuleUpsellBannerLearnMore = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.LISTING_MODULE_UPSELL_BANNER_LEARN_MORE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_property_overview',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'tenant_placement_listing_block',
      trigger_source_nm: 'zrm_property_overview',
    },
    semantic: {
      semantic_event_nm: 'tenant_placement_zillow_entrypoint',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6755
const TenantPlacementListingModuleUpsellBanner = ({
  user,
  currentProperty,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.LISTING_MODULE_UPSELL_BANNER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_property_overview',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'tenant_placement_listing_block',
      trigger_source_nm: 'zrm_property_overview',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6462
const TenantPlacementDashboardUpsellBanner = ({
  user,
  currentProperty,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.DASHBOARD_UPSELL_BANNER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_property_overview',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'tenant_placement_upsell_banner',
      trigger_source_nm: 'zrm_property_overview',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6398
const TenantPlacementDashboardUpsellBannerLearnMore = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.DASHBOARD_UPSELL_BANNER_LEARN_MORE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_property_overview',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'tenant_placement_upsell_banner',
      trigger_source_nm: 'learn_more',
    },
    semantic: {
      semantic_event_nm: 'tenant_placement_zillow_entrypoint',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6460
const TenantPlacementReactivateListingUpsellModal = ({
  currentProperty,
  user,
}: {
  user: ZrmClickstreamUser;
  currentProperty: ZrmClickstreamListing;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.REACTIVATE_LISTING_UPSELL_MODAL,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'how_list_modal_reactivate',
      trigger_source_nm: 'posting_path',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6396
const TenantPlacementReactivationLearnMoreButton = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.REACTIVATION_LEARN_MORE_BUTTON,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'how_list_modal_reactivate',
      trigger_source_nm: 'learn_more',
    },
    semantic: {
      semantic_event_nm: 'tenant_placement_zillow_entrypoint',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6395
const TenantPlacementReactivateListingButton = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.REACTIVATE_LISTING_BUTTON,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'how_list_modal_reactivate',
      trigger_source_nm: 'post_listing',
    },
    semantic: {
      semantic_event_nm: 'tenant_placement_zillow_entrypoint',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6459
const TenantPlacementNewListingUpsellModal = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.NEW_LISTING_UPSELL_MODAL,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'impression',
      trigger_object_nm: 'how_list_modal_newlisting',
      trigger_source_nm: 'posting_path',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6394
const TenantPlacementNewListingLearnMoreButton = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.NEW_LISTING_LEARN_MORE_BUTTON,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'how_list_modal_newlisting',
      trigger_source_nm: 'learn_more',
    },
    semantic: {
      semantic_event_nm: 'tenant_placement_zillow_entrypoint',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6393
const TenantPlacementPostNewListingButton = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.POST_NEW_LISTING_BUTTON,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'how_list_modal_newlisting',
      trigger_source_nm: 'post_listing',
    },
    semantic: {
      semantic_event_nm: 'tenant_placement_zillow_entrypoint',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6388
const TenantPlacementGoToDoorstead = ({
  user,
  pageLocation,
}: {
  user: ZrmClickstreamUser;
  pageLocation: PageLocation;
}) =>
  trackEvent.user({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.GO_TO_DOORSTEAD,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'tenant_placement_landing_page',
      trigger_type_nm: 'interaction',
      trigger_object_nm: pageLocation,
      trigger_source_nm: 'get_started',
    },
    semantic: {
      semantic_event_nm: 'tenant_placement_doorstead_entrypoint',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7509
const TenantPlacementGoToNomad = ({ user, pageLocation }: { user: ZrmClickstreamUser; pageLocation: PageLocation }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.GO_TO_NOMAD,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'tenant_placement_landing_page',
      trigger_type_nm: 'interaction',
      trigger_object_nm: pageLocation,
      trigger_source_nm: 'get_started',
    },
    semantic: {
      semantic_event_nm: 'tenant_placement_nomad_entrypoint',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7510
const TenantPlacementStateNotSupported = ({
  user,
  pageLocation,
}: {
  user: ZrmClickstreamUser;
  pageLocation: PageLocation;
}) =>
  trackEvent.user({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.STATE_NOT_SUPPORTED,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'tenant_placement_landing_page',
      trigger_type_nm: 'interaction',
      trigger_object_nm: pageLocation,
      trigger_source_nm: 'get_started',
    },
    semantic: {
      semantic_event_nm: 'tenant_placement_statenotsupported_entrypoint',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6437
const TenantPlacementViewPage = ({ user }: { user: ZrmClickstreamUser }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.VIEW_PAGE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'tenant_placement_landing_page',
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'tenant_placement_landing_page',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_monetization'],
    },
    ...getZrmUserInfo(user),
  });

const TenantPlacementCSEvents = {
  TenantPlacementDashboardUpsellBanner,
  TenantPlacementDashboardUpsellBannerLearnMore,
  TenantPlacementGoToDoorstead,
  TenantPlacementGoToNomad,
  TenantPlacementListingModuleUpsellBanner,
  TenantPlacementListingModuleUpsellBannerLearnMore,
  TenantPlacementNewListingLearnMoreButton,
  TenantPlacementNewListingUpsellModal,
  TenantPlacementPostNewListingButton,
  TenantPlacementStateNotSupported,
  TenantPlacementReactivateListingButton,
  TenantPlacementReactivateListingUpsellModal,
  TenantPlacementReactivationLearnMoreButton,
  TenantPlacementViewPage,
};

// Clickstream visualizer data-eventid attributes
TenantPlacementListingModuleUpsellBannerLearnMore.data_event_id = EVENT_IDS.LISTING_MODULE_UPSELL_BANNER_LEARN_MORE;
TenantPlacementListingModuleUpsellBanner.data_event_id = EVENT_IDS.LISTING_MODULE_UPSELL_BANNER;
TenantPlacementDashboardUpsellBanner.data_event_id = EVENT_IDS.DASHBOARD_UPSELL_BANNER;
TenantPlacementDashboardUpsellBannerLearnMore.data_event_id = EVENT_IDS.DASHBOARD_UPSELL_BANNER_LEARN_MORE;
TenantPlacementReactivateListingUpsellModal.data_event_id = EVENT_IDS.REACTIVATE_LISTING_UPSELL_MODAL;
TenantPlacementReactivationLearnMoreButton.data_event_id = EVENT_IDS.REACTIVATION_LEARN_MORE_BUTTON;
TenantPlacementReactivateListingButton.data_event_id = EVENT_IDS.REACTIVATE_LISTING_BUTTON;
TenantPlacementNewListingUpsellModal.data_event_id = EVENT_IDS.NEW_LISTING_UPSELL_MODAL;
TenantPlacementNewListingLearnMoreButton.data_event_id = EVENT_IDS.NEW_LISTING_LEARN_MORE_BUTTON;
TenantPlacementPostNewListingButton.data_event_id = EVENT_IDS.POST_NEW_LISTING_BUTTON;
TenantPlacementGoToDoorstead.data_event_id = EVENT_IDS.GO_TO_DOORSTEAD;
TenantPlacementGoToNomad.data_event_id = EVENT_IDS.GO_TO_NOMAD;
TenantPlacementStateNotSupported.data_event_id = EVENT_IDS.STATE_NOT_SUPPORTED;
TenantPlacementViewPage.data_event_id = EVENT_IDS.VIEW_PAGE;

export default TenantPlacementCSEvents;
